// src/sectors/pilates/members/modal/MemberEditModal/MemberEditModal.jsx
import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { SoftIcon } from '../MemberCreateModal/ui/SoftIcon';
import { MemberInfoCard } from '../MemberCreateModal/parts/MemberInfoCard';
import { MemberSummaryCard } from '../MemberCreateModal/parts/MemberSummaryCard';
import { TrainerSelectCard } from '../MemberCreateModal/parts/TrainerSelectCard';

import { membersApi } from 'sectors/pilates/members/api/membersApi';
import { uiBus } from 'shared/api/uiBus';

/* ---------------- utils ---------------- */
function onlyDigits(s) {
  return String(s || '').replace(/\D+/g, '');
}

function isValidTurkishPhone(phone) {
  return /^05\d{9}$/.test(onlyDigits(phone));
}

/* ---------------- form shape ---------------- */
const emptyForm = () => ({
  id: null,
  name: '',
  surname: '',
  birthDate: '',
  phone: '',
  notes: '',
  assignedTrainerId: '',
  membershipStatus: true,

  totalTry: null,
  approvedPaidTry: null,
  pendingPaidTry: null,
  debtTry: null,
  paymentStatus: null,
});

/* ---------------- response -> form ---------------- */
function memberEditDetailToForm(res) {
  const base = emptyForm();

  return {
    ...base,
    id: res?.id ?? null,
    name: String(res?.name || '').trim(),
    surname: String(res?.surname || '').trim(),
    birthDate: res?.birthDate ? String(res.birthDate).slice(0, 10) : '',
    phone: res?.phone ? String(res.phone) : '',
    notes: res?.notes ? String(res.notes) : '',
    membershipStatus:
      typeof res?.membershipStatusActive === 'boolean' ? res.membershipStatusActive : true,
    assignedTrainerId: res?.assignedTrainerId == null ? '' : Number(res.assignedTrainerId),

    totalTry: res?.totalTry ?? null,
    approvedPaidTry: res?.approvedPaidTry ?? null,
    pendingPaidTry: res?.pendingPaidTry ?? null,
    debtTry: res?.debtTry ?? null,
    paymentStatus: res?.paymentStatus ?? null,
  };
}

function hasMeaningfulDiff(a, b) {
  if (!a || !b) return true;

  const keys = ['name', 'surname', 'birthDate', 'phone', 'notes', 'assignedTrainerId'];

  for (const k of keys) {
    if (String(a?.[k] ?? '') !== String(b?.[k] ?? '')) return true;
  }

  if (!!a?.membershipStatus !== !!b?.membershipStatus) return true;

  return false;
}

/* ---------------- diff builder ---------------- */
function buildPatch(initial, current) {
  const patch = {};

  const keys = ['name', 'surname', 'birthDate', 'phone', 'notes', 'assignedTrainerId'];

  keys.forEach((k) => {
    if (String(initial?.[k] ?? '') !== String(current?.[k] ?? '')) {
      patch[k] = current?.[k] ?? '';
    }
  });

  if (!!initial?.membershipStatus !== !!current?.membershipStatus) {
    patch.membershipStatusActive = !!current?.membershipStatus;
  }

  return patch;
}

export function MemberEditModal({ open, onClose, member, onSave }) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isWide = useMediaQuery(theme.breakpoints.up('lg'));

  const [form, setForm] = React.useState(emptyForm());
  const initialRef = React.useRef(emptyForm());

  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const memberId = React.useMemo(
    () => member?.id ?? member?._id ?? member?.memberId ?? null,
    [member]
  );

  const set = React.useCallback((k, v) => {
    setForm((s) => ({ ...s, [k]: v }));
  }, []);

  React.useEffect(() => {
    if (!open) return;

    let alive = true;

    (async () => {
      setLoading(true);
      setSaving(false);

      try {
        if (!memberId) throw new Error('Üye id bulunamadı.');

        const res = await membersApi.editDetail(memberId);
        if (!alive) return;

        const next = memberEditDetailToForm(res);
        initialRef.current = next;
        setForm(next);
      } catch (e) {
        console.error(e);
        if (!alive) return;

        uiBus.emit('toast', {
          type: 'error',
          message: e?.response?.data?.message || e?.message || 'Üye bilgileri alınamadı.',
        });

        const next = emptyForm();
        initialRef.current = next;
        setForm(next);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, memberId]);

  const isValid =
    String(form.name || '').trim().length >= 2 && String(form.surname || '').trim().length >= 2;

  const isDirty = React.useMemo(() => hasMeaningfulDiff(initialRef.current, form), [form]);

  const canSave = isValid && isDirty && !saving && !loading;

  const safeClose = React.useCallback(() => {
    if (saving || loading) return;
    onClose?.();
  }, [saving, loading, onClose]);

  const onClickSave = React.useCallback(async () => {
    if (String(form.name || '').trim().length < 2) {
      uiBus.emit('toast', {
        type: 'warning',
        message: 'Lütfen üyenin adını en az 2 karakter olacak şekilde gir.',
      });
      return;
    }

    if (String(form.surname || '').trim().length < 2) {
      uiBus.emit('toast', {
        type: 'warning',
        message: 'Lütfen üyenin soyadını en az 2 karakter olacak şekilde gir.',
      });
      return;
    }

    const phoneDigits = onlyDigits(form.phone);
    if (phoneDigits.length > 0 && !isValidTurkishPhone(phoneDigits)) {
      uiBus.emit('toast', {
        type: 'warning',
        message: 'Telefon numarasını kontrol edelim. Türkiye formatı için 05xx xxx xx xx olmalı.',
      });
      return;
    }

    const patch = buildPatch(initialRef.current, form);

    setSaving(true);
    try {
      await onSave?.({
        id: memberId ?? form.id,
        patch,
      });

      uiBus.emit('toast', {
        type: 'success',
        message: 'Üye bilgileri başarıyla güncellendi.',
      });

      onClose?.();
    } catch (e) {
      console.error(e);

      uiBus.emit('toast', {
        type: 'error',
        message: e?.response?.data?.message || e?.message || 'Kaydetme sırasında hata oluştu.',
      });
    } finally {
      setSaving(false);
    }
  }, [form, onSave, memberId, onClose]);

  const headerBg = 'linear-gradient(180deg, rgba(124,58,237,.16), rgba(124,58,237,.06))';

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={saving || loading ? undefined : safeClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth={isWide ? 'md' : 'sm'}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '22px',
          border: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          boxShadow: theme.shadows[12],
          width: isMobile ? '100%' : isWide ? '980px' : '820px',
          maxWidth: isMobile ? '100%' : '95vw',
        },
      }}
    >
      <DialogTitle sx={{ p: 0, background: headerBg }}>
        <MDBox sx={{ px: 2, py: 1.6 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDBox display="flex" alignItems="center" gap={1.2} sx={{ minWidth: 0 }}>
              <SoftIcon icon="edit" tone="violet" />
              <MDBox sx={{ minWidth: 0 }}>
                <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                  Üye Düzenle
                </MDTypography>

                <MDBox display="flex" gap={1} mt={0.6} sx={{ flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    icon={<Icon sx={{ fontSize: 16 }}>badge</Icon>}
                    label="Profil"
                  />
                  <Chip
                    size="small"
                    icon={<Icon sx={{ fontSize: 16 }}>person_search</Icon>}
                    label="Eğitmen"
                  />
                  {loading && (
                    <Chip
                      size="small"
                      icon={<Icon sx={{ fontSize: 16 }}>hourglass_top</Icon>}
                      label="Yükleniyor"
                    />
                  )}
                </MDBox>
              </MDBox>
            </MDBox>

            <MDButton
              variant="outlined"
              color="dark"
              onClick={safeClose}
              disabled={saving || loading}
              sx={{ borderRadius: '14px' }}
            >
              Kapat
            </MDButton>
          </MDBox>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2 }}>
        {loading ? (
          <MDBox sx={{ display: 'grid', gap: 1.25 }}>
            <Skeleton variant="rounded" height={140} />
            <Skeleton variant="rounded" height={180} />
            <Skeleton variant="rounded" height={160} />
          </MDBox>
        ) : (
          <MDBox
            sx={{
              display: 'grid',
              gap: 1.25,
              gridTemplateColumns: { xs: '1fr', lg: '1.15fr .85fr' },
              alignItems: 'start',
            }}
          >
            <MDBox sx={{ display: 'grid', gap: 1.25 }}>
              <MemberInfoCard form={form} set={set} />
            </MDBox>

            <MDBox sx={{ display: 'grid', gap: 1.25 }}>
              <TrainerSelectCard
                open={open}
                value={form.assignedTrainerId}
                onChangeValue={(v) => set('assignedTrainerId', v)}
              />

              <MemberSummaryCard form={form} canSave={canSave} saving={saving || loading} />
            </MDBox>
          </MDBox>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <MDButton
          variant="outlined"
          color="dark"
          sx={{ borderRadius: '14px' }}
          onClick={safeClose}
          disabled={saving || loading}
        >
          İptal
        </MDButton>

        <MDBox display="flex" gap={1}>
          <MDButton
            variant="outlined"
            color="dark"
            sx={{ borderRadius: '14px' }}
            onClick={() => {
              if (saving || loading) return;
              setForm(initialRef.current);
            }}
            disabled={saving || loading}
          >
            <Icon sx={{ mr: 0.5, fontSize: 18 }}>restore</Icon>
            Geri Al
          </MDButton>

          <MDButton
            variant="gradient"
            color="success"
            sx={{ borderRadius: '14px' }}
            disabled={!canSave || saving || loading}
            onClick={onClickSave}
          >
            <Icon sx={{ mr: 0.5, fontSize: 18 }}>save</Icon>
            Kaydet
          </MDButton>
        </MDBox>
      </DialogActions>
    </Dialog>
  );
}
